import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { z } from 'zod';

import { putProfile } from '@/auth/api';
import { useAuth } from '@/auth/use-auth';
import { FormTextField } from '@/components/form/form-text-field';
import { BevelWindow } from '@/components/prototype/bevel-window';
import { Button } from '@/components/ui/button';
import { Form } from '@/components/ui/form';
import { applyApiErrors } from '@/pages/auth/form-errors';

const schema = z.object({
  name: z.string().min(1).max(255),
  display_name: z.string().max(255).optional(),
  email: z.string().email(),
});

type ProfileValues = z.infer<typeof schema>;

export function ProfileSection() {
  const { t } = useTranslation('auth');
  const { user, refresh } = useAuth();

  const form = useForm<ProfileValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      name: user?.name ?? '',
      display_name: user?.display_name ?? '',
      email: user?.email ?? '',
    },
  });

  const onSubmit = form.handleSubmit(async (values) => {
    try {
      await putProfile({
        name: values.name,
        display_name: values.display_name || undefined,
        email: values.email,
      });
      await refresh();
      toast.success(t('settings.profileSaved'));
    } catch (error) {
      applyApiErrors(error, form.setError, (message) => toast.error(message));
    }
  });

  return (
    <BevelWindow title={t('settings.profile')}>
      <Form {...form}>
        <form onSubmit={onSubmit} className="flex flex-col gap-4" noValidate>
          <FormTextField
            control={form.control}
            name="name"
            label={t('fields.name')}
            autoComplete="username"
          />
          <FormTextField
            control={form.control}
            name="display_name"
            label={t('fields.displayName')}
          />
          <FormTextField
            control={form.control}
            name="email"
            label={t('fields.email')}
            type="email"
            autoComplete="email"
          />
          <div>
            <Button type="submit" variant="primary" disabled={form.formState.isSubmitting}>
              {t('settings.saveProfile')}
            </Button>
          </div>
        </form>
      </Form>
    </BevelWindow>
  );
}
